<template>
  <Dialog :visible="manageSkillDialogVisible"
          @update:visible="handleClose"
          :closeOnEscape="manageSkillDialogCloseOnEscape">
    <template #header>
      {{ manageSkillId ? 'Edit' : 'Add' }} Agent Skill
    </template>

    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <div class="form-element">
        Agent Skill Name:
        <InputText v-model="manageSkillName" :invalid="manageSkillNameInvalid" :disabled="manageSkillSubmitting" class="form-element full-width" type="text"
                   @change="manageSkillNameChange"/>
        <div v-if="manageSkillNameInvalid">
          <InlineMessage severity="error">Agent Skill Name is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Machine Name:
        <InputText v-model="manageSkillMachineName" :invalid="manageSkillMachineNameInvalid" :disabled="manageSkillSubmitting" class="form-element full-width" type="text"
                   @change="manageSkillMachineNameChange"/>
        <div v-if="manageSkillMachineNameInvalid">
          <InlineMessage severity="error">Machine Name is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Message to User:
        <Textarea v-model="manageSkillMessageToUser" :invalid="manageSkillMessageToUserInvalid" :disabled="manageSkillSubmitting" class="form-element full-width" type="text"
                 @change="manageSkillMessageToUserChange"
                 auto-resize
        />
        <div v-if="manageSkillMessageToUserInvalid">
          <InlineMessage severity="error">Message to User is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Short Description:
        <InputText v-model="manageSkillShortDescription" :invalid="manageSkillShortDescriptionInvalid" :disabled="manageSkillSubmitting" class="form-element full-width" type="text"
                   @change="manageSkillShortDescriptionChange"/>
        <div v-if="manageSkillShortDescriptionInvalid">
          <InlineMessage severity="error">Agent Skill Short Description is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Long Description:
        <Textarea v-model="manageSkillLongDescription" :invalid="manageSkillLongDescriptionInvalid" :disabled="manageSkillSubmitting" class="form-element full-width" type="text"
                   @change="manageSkillLongDescriptionChange"
                  auto-resize
        />
      </div>

      <div class="form-element">
        Output Override (leave blank to default to last step result):
        <Textarea v-model="manageSkillOutput" :disabled="manageSkillSubmitting" class="form-element full-width"
                  auto-resize />
      </div>

      <div class="form-element">
        Type:
        <SelectButton v-model="manageSkillOrganizational" :options="skillTypeOptions" :disabled="manageSkillSubmitting" option-label="label" option-value="value" class="form-element" />
      </div>

      <div v-if="isEditingUserSkill" class="form-element mt">
        <InlineMessage severity="warn">Please edit the steps for this Agent Skill in the OrgBrain app.</InlineMessage>
      </div>

      <div v-else class="form-element">
        <DataTable :value="manageSkillSteps" table-style="width: 100%"
                   @row-reorder="reorderSkillSteps"
                   :reorderableRows="!manageSkillSubmitting">
          <template #header>
            <div class="header-container">
              <div class="align-right full-width">
                <Button @click="addSkillStep" :disabled="manageSkillSubmitting"><i class="pi pi-plus"></i> Add Step</Button>
              </div>
            </div>
          </template>
          <Column row-reorder header-style="width: 2rem" :reorderable-column="false" />
          <Column header="Order">
            <template #body="slotProps">
              {{ slotProps.index }}
            </template>
          </Column>
          <Column header="Name">
            <template #body="slotProps">
              <InputText :invalid="skillStepNameIsInvalid(slotProps.index)"
                         :disabled="manageSkillSubmitting"
                         v-model="slotProps.data.name"
                         @change="skillStepNameChange(slotProps.index)"
              ></InputText>
              <div v-if="skillStepNameIsInvalid(slotProps.index)">
                <InlineMessage severity="error">Name is required.</InlineMessage>
              </div>
            </template>
          </Column>
          <Column header="Machine Name">
            <template #body="slotProps">
              <InputText :invalid="skillStepMachineNameIsInvalid(slotProps.index) || skillStepMachineNameIsDuplicate(slotProps.index)"
                         :disabled="manageSkillSubmitting"
                         v-model="slotProps.data.machineName"
                         @change="skillStepMachineNameChange(slotProps.index)"
              ></InputText>
              <div v-if="skillStepMachineNameIsInvalid(slotProps.index)">
                <InlineMessage severity="error">Machine Name is required.</InlineMessage>
              </div>
              <div v-else-if="skillStepMachineNameIsDuplicate(slotProps.index)">
                <InlineMessage severity="error">Machine Name must be unique.</InlineMessage>
              </div>
            </template>
          </Column>
          <Column header="Type">
            <template #body="slotProps">
              <Dropdown :invalid="skillStepTypeIsInvalid(slotProps.index)" 
                        :disabled="manageSkillSubmitting"
                        v-model="slotProps.data.typeId"
                        :options="skillStepTypes"
                        option-label="name" option-value="id"
                        @change="skillStepTypeChange(slotProps.index)"
              ></Dropdown>
              <div v-if="skillStepTypeIsInvalid(slotProps.index)">
                <InlineMessage severity="error">Type is required.</InlineMessage>
              </div>
            </template>
          </Column>
          <Column header="Disable Modes">
            <template #body="slotProps">
              <Checkbox v-model="slotProps.data.disableModes" :disabled="manageSkillSubmitting" :binary="true" />
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <div class="flex gap-2">
                <Button label="Configure" severity="info" size="small" :disabled="manageSkillSubmitting" @click="openStepConfigDialog(slotProps.index)"></Button>
                <Button class="ml" icon="pi pi-trash" severity="danger" size="small" :disabled="manageSkillSubmitting" @click="confirmDeleteStep(slotProps.index)" title="Delete step"></Button>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" :disabled="manageSkillSubmitting" @click="handleClose"></Button>
        <Button :disabled="manageSkillSubmitting" :loading="manageSkillSubmitting" label="Save" type="button" @click="saveSkill"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="stepConfigDialogVisible" @update:visible="closeStepConfigDialog" :style="{width: '80vw'}" modal :closeOnEscape="stepConfigDialogCloseOnEscape">
    <template #header>
      Configure Step: {{ currentStepConfig?.name || 'New Step' }}
    </template>

    <ScrollPanel class="scroll-panel" style="max-height: 70vh">
      <div class="form-element">
        <label>Description:</label>
        <InputText v-model="currentStepConfig.description" class="full-width"></InputText>
      </div>

      <div v-if="showSystemMessage(currentStepConfig.typeId)" class="form-element">
        <label>System Message:</label>
        <Textarea v-model="currentStepConfig.systemMessage" 
                  auto-resize
                  class="full-width"
        ></Textarea>
      </div>

      <div v-if="showUserMessage(currentStepConfig.typeId)" class="form-element">
        <label>{{ getUserMessageLabel(currentStepConfig.typeId) }}:</label>
        <Textarea v-model="currentStepConfig.userMessage" 
                  auto-resize
                  class="full-width"
        ></Textarea>
        
        <!-- Available Tokens Info -->
        <Accordion>
          <AccordionTab header="Available Tokens">
            <div class="tokens-info">
              <p><strong>Standard Tokens:</strong></p>
              <ul>
                <li><code>{user_message}</code></li>
                <li><code>{system_prompt}</code></li>
              </ul>
              
              <div v-if="currentStepConfig.arrayInputStep">
                <p><strong>Array Step Tokens:</strong></p>
                <ul>
                  <li><code>{array_value}</code> - Current value in the array</li>
                  <li><code>{array_index}</code> - Current index in the array</li>
                </ul>
              </div>
              
              <div v-if="getAvailableStepTokens(currentStepIndex).length > 0">
                <p><strong>Previous Step Results:</strong></p>
                <ul>
                  <li v-for="token in getAvailableStepTokens(currentStepIndex)" :key="token.name">
                    <code>{{ token.name }}</code> - {{ token.description }}
                  </li>
                </ul>
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      </div>

      <!-- Type-specific fields -->
      <div v-if="currentStepConfig.typeId" class="form-element">
        <h3>Additional Settings</h3>
        
        <!-- Array Input Step -->
        <div v-if="showArrayInputField(currentStepConfig.typeId)" class="form-element">
          <label>Array Input Step:</label>
          <Dropdown v-model="currentStepConfig.arrayInputStep" 
                    :options="getPreviousStepMachineNames(currentStepIndex)"
                    placeholder="Select step"
                    :show-clear="true"
                    editable
                    class="full-width"
          ></Dropdown>
        </div>

        <!-- Jump Fields -->
        <template v-if="isJumpStepType(currentStepConfig.typeId)">
          <div class="form-element">
            <label>Destination Step Name:</label>
            <InputText v-model="currentStepConfig.jumpToStep" placeholder="Step name" class="full-width"></InputText>
          </div>
          <div v-if="showJumpCompareValue(currentStepConfig.typeId)" class="form-element">
            <label>Jump Compare Value:</label>
            <InputText v-model="currentStepConfig.jumpCompareValue" placeholder="Value" class="full-width"></InputText>
          </div>
        </template>

        <!-- Vector Source -->
        <div v-if="isVectorQueryStepType(currentStepConfig.typeId)" class="form-element">
          <label>Vector Source:</label>
          
          <div class="vector-source-options">
            <div class="flex align-items-center">
              <RadioButton v-model="vectorSourceType" inputId="vs_all" value="all" />
              <label for="vs_all" class="ml-2">All Files</label>
            </div>
            
            <div class="flex align-items-center">
              <RadioButton v-model="vectorSourceType" inputId="vs_input_files" value="input_files" />
              <label for="vs_input_files" class="ml-2">All Input Files</label>
            </div>
            
            <div class="vector-source-option">
              <div class="flex align-items-center">
                <RadioButton v-model="vectorSourceType" inputId="vs_input_file_index" value="input_file_index" />
                <label for="vs_input_file_index" class="ml-2">Single Input File</label>
              </div>
              <div v-if="vectorSourceType === 'input_file_index'" class="ml-2 mt">
                <label>File Index (0-based):</label>
                <InputNumber v-model="vectorSourceFileIndex" class="full-width" :min="0" placeholder="0" />
              </div>
            </div>
            
            <div class="vector-source-option">
              <div class="flex align-items-center">
                <RadioButton v-model="vectorSourceType" inputId="vs_files" value="files" />
                <label for="vs_files" class="ml-2">Specific Files</label>
              </div>
              <div v-if="vectorSourceType === 'files'" class="ml-2 mt">
                <Button label="Select Files" icon="pi pi-folder-open" @click="openFileSelector" :disabled="loadingSelectedItems" class="p-button-sm mb-2" />
                <ProgressSpinnerContainer v-if="loadingSelectedItems" style="min-height: 60px;" />
                <div v-else-if="selectedFilesDisplay.length > 0" class="selected-items-display">
                  <small class="text-muted">Selected Files ({{ selectedFilesDisplay.length }}):</small>
                  <div class="selected-items-list">
                    <Chip v-for="file in selectedFilesDisplay" :key="file.id" :label="file.filename" removable @remove="removeSelectedFile(file.id)" />
                  </div>
                </div>
                <small v-else class="text-muted">No files selected</small>
              </div>
            </div>
            
            <div class="vector-source-option">
              <div class="flex align-items-center">
                <RadioButton v-model="vectorSourceType" inputId="vs_directories" value="directories" />
                <label for="vs_directories" class="ml-2">Specific Directories</label>
              </div>
              <div v-if="vectorSourceType === 'directories'" class="ml-2 mt">
                <Button label="Select Directories" icon="pi pi-folder-open" @click="openDirectorySelector" :disabled="loadingSelectedItems" class="p-button-sm mb-2" />
                <ProgressSpinnerContainer v-if="loadingSelectedItems" style="min-height: 60px;" />
                <div v-else-if="selectedDirectoriesDisplay.length > 0" class="selected-items-display">
                  <small class="text-muted">Selected Directories ({{ selectedDirectoriesDisplay.length }}):</small>
                  <div class="selected-items-list">
                    <Chip v-for="dir in selectedDirectoriesDisplay" :key="dir.id" :label="dir.title" removable @remove="removeSelectedDirectory(dir.id)" />
                  </div>
                </div>
                <small v-else class="text-muted">No directories selected</small>
              </div>
            </div>
            
            <div class="vector-source-option">
              <div class="flex align-items-center">
                <RadioButton v-model="vectorSourceType" inputId="vs_knowledge_sources" value="knowledge_sources" />
                <label for="vs_knowledge_sources" class="ml-2">Knowledge Sources</label>
              </div>
              <div v-if="vectorSourceType === 'knowledge_sources'" class="ml-2 mt">
                <label>Knowledge Sources:</label>
                <MultiSelect v-model="vectorSourceKnowledgeSourceIds" 
                             :options="knowledgeSources" 
                             option-label="name" 
                             option-value="id"
                             placeholder="Select knowledge sources" 
                             class="full-width"
                             display="chip"></MultiSelect>
              </div>
            </div>
          </div>
        </div>

        <!-- Cached Parent Step -->
        <div v-if="showCachedParentField(currentStepConfig.typeId)" class="form-element">
          <label>Cached Parent Step:</label>
          <Dropdown v-model="currentStepConfig.cachedParentStep" 
                    :options="getPreviousStepMachineNames(currentStepIndex)"
                    placeholder="Select step"
                    :show-clear="true"
                    editable
                    class="full-width"
          ></Dropdown>
        </div>

        <!-- Lookup Fields -->
        <template v-if="isLookupStepType(currentStepConfig.typeId)">
          <div class="form-element">
            <label>Lookup Table:</label>
            <InputText v-model="currentStepConfig.lookupTableName" placeholder="Table name" class="full-width"></InputText>
          </div>
          <div class="form-element">
            <label>Lookup Output Columns:</label>
            <InputText v-model="currentStepConfig.lookupOutputColumns" placeholder="Column names" class="full-width"></InputText>
          </div>
          <div class="form-element">
            <label><span class="mr">Lookup Config:</span></label>
            <Button label="Configure Filters & Sorting" severity="info" @click="openLookupDialog(currentStepIndex)"></Button>
          </div>
          <div class="form-element">
            <label>Lookup Limit:</label>
            <InputNumber v-model="currentStepConfig.lookupLimit" placeholder="Limit" class="full-width"></InputNumber>
          </div>
        </template>

        <!-- Skill to Execute -->
        <div v-if="isSkillStepType(currentStepConfig.typeId)" class="form-element">
          <label>Skill to Execute:</label>
          <Dropdown v-model="currentStepConfig.aiSkillToExecuteId" 
                    :options="getAvailableSkillsForExecution()"
                    option-label="name" 
                    option-value="id"
                    placeholder="Select skill"
                    :show-clear="true"
                    class="full-width"
          ></Dropdown>
        </div>

        <!-- Expand Values Source Step -->
        <div v-if="isExpandValuesStepType(currentStepConfig.typeId)" class="form-element">
          <label>Source Step:</label>
          <Dropdown v-model="currentStepConfig.expandValuesSourceStep" 
                    :options="getPreviousStepMachineNames(currentStepIndex)"
                    placeholder="Select step"
                    :show-clear="true"
                    editable
                    class="full-width"
          ></Dropdown>
        </div>

        <!-- Split Text Max Tokens -->
        <div v-if="isSplitTextStepType(currentStepConfig.typeId)" class="form-element">
          <label>Max Tokens:</label>
          <InputNumber v-model="currentStepConfig.splitTextMaxTokens" 
                       placeholder="Max tokens"
                       class="full-width"
          ></InputNumber>
        </div>

        <!-- File Contents Input File -->
        <div v-if="isFileContentsStepType(currentStepConfig.typeId)" class="form-element">
          <label>Input File:</label>
          <InputNumber v-model="currentStepConfig.fileContentsInputFile" 
                       :min="0"
                       placeholder="0"
                       class="full-width"
          ></InputNumber>
        </div>
      </div>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button label="Close" severity="secondary" @click="closeStepConfigDialog"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="lookupDialogVisible" @update:visible="closeLookupDialog" :style="{width: '90vw'}" modal :closeOnEscape="lookupDialogCloseOnEscape">
    <template #header>
      Configure Lookup Step
    </template>

    <div class="form-element mb">
      <SelectButton v-model="lookupEditorMode" :options="lookupEditorModes" option-label="label" option-value="value" />
    </div>

    <ScrollPanel class="scroll-panel" style="max-height: 70vh">
      <div v-if="lookupEditorMode === 'visual'">
      <!-- Filters Section -->
      <div class="form-element mb">
        <h3>Filters</h3>
        <DataTable :value="lookupFiltersArray">
          <template #header>
            <div class="align-right">
              <Button @click="addLookupFilter" size="small"><i class="pi pi-plus"></i> Add Filter</Button>
            </div>
          </template>
          <Column header="Column">
            <template #body="slotProps">
              <InputText v-model="slotProps.data.column" placeholder="Column name" class="full-width"></InputText>
            </template>
          </Column>
          <Column header="Condition">
            <template #body="slotProps">
              <Dropdown v-model="slotProps.data.condition" :options="conditionOptions" placeholder="Select" class="full-width"></Dropdown>
            </template>
          </Column>
          <Column header="Value(s)">
            <template #body="slotProps">
              <!-- Simple text for = and LIKE -->
              <InputText v-if="slotProps.data.condition === '=' || slotProps.data.condition === 'LIKE'" 
                         v-model="slotProps.data.value" 
                         placeholder="Value" class="full-width"></InputText>
              
              <!-- Chips for IN -->
              <div v-else-if="slotProps.data.condition === 'IN'">
                <Chips v-model="slotProps.data.value" 
                       placeholder="Type value and press Enter" 
                       class="full-width"></Chips>
                <small class="text-muted">Press Enter after typing each value to add it to the list</small>
              </div>
              
              <!-- Two inputs for BETWEEN -->
              <div v-else-if="slotProps.data.condition === 'BETWEEN'" class="flex gap-2">
                <InputText v-model="slotProps.data.value[0]" placeholder="From" class="flex-1"></InputText>
                <InputText v-model="slotProps.data.value[1]" placeholder="To" class="flex-1"></InputText>
              </div>
              
              <!-- GEO_WITHIN_DISTANCE fields -->
              <div v-else-if="slotProps.data.condition === 'GEO_WITHIN_DISTANCE'">
                <div class="mb-2">
                  <SelectButton v-model="slotProps.data.geoMode" :options="geoModeOptions" option-label="label" option-value="value" />
                </div>
                <div v-if="slotProps.data.geoMode === 'latlon'" class="mb-2">
                  <InputText v-model="slotProps.data.latlon" placeholder="LatLon (e.g., {STEP:result})" class="full-width"></InputText>
                </div>
                <div v-else class="flex gap-2 mb-2">
                  <InputText v-model="slotProps.data.lat" placeholder="Latitude" class="flex-1"></InputText>
                  <InputText v-model="slotProps.data.lon" placeholder="Longitude" class="flex-1"></InputText>
                </div>
                <InputNumber v-model="slotProps.data.distance_meters" placeholder="Distance (meters)" class="full-width"></InputNumber>
              </div>
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button severity="danger" size="small" @click="removeLookupFilter(slotProps.index)"><i class="pi pi-trash"></i></Button>
            </template>
          </Column>
        </DataTable>
      </div>

      <!-- Output Distance Section -->
      <div class="form-element mb">
        <h3>
          <Checkbox v-model="lookupOutputDistanceEnabled" :binary="true" inputId="outputDistanceEnabled" />
          <label for="outputDistanceEnabled" class="ml-2">Output Distance (Computed)</label>
        </h3>
        <div v-if="lookupOutputDistanceEnabled" class="ml-2">
          <div class="form-element">
            Column:
            <InputText v-model="lookupOutputDistance.column" placeholder="GEOM" class="full-width"></InputText>
          </div>
          <div class="form-element">
            <SelectButton v-model="lookupOutputDistance.geoMode" :options="geoModeOptions" option-label="label" option-value="value" />
          </div>
          <div v-if="lookupOutputDistance.geoMode === 'latlon'" class="form-element">
            LatLon:
            <InputText v-model="lookupOutputDistance.latlon" placeholder="{STEP:result}" class="full-width"></InputText>
          </div>
          <div v-else class="form-element">
            <div class="flex gap-2">
              <div class="flex-1">
                Latitude:
                <InputText v-model="lookupOutputDistance.lat" placeholder="Latitude" class="full-width"></InputText>
              </div>
              <div class="flex-1">
                Longitude:
                <InputText v-model="lookupOutputDistance.lon" placeholder="Longitude" class="full-width"></InputText>
              </div>
            </div>
          </div>
          <div class="form-element">
            Alias:
            <InputText v-model="lookupOutputDistance.alias" placeholder="DISTANCE_MILES" class="full-width"></InputText>
          </div>
          <div class="form-element">
            SRID:
            <InputNumber v-model="lookupOutputDistance.srid" placeholder="4326" class="full-width"></InputNumber>
          </div>
          <div class="form-element">
            Unit:
            <Dropdown v-model="lookupOutputDistance.unit" :options="['MILE', 'KM']" placeholder="Select unit" class="full-width"></Dropdown>
          </div>
        </div>
      </div>

      <!-- Sort Columns Section -->
      <div class="form-element mb">
        <h3>Sort Columns</h3>
        <DataTable :value="lookupSortColumns" @row-reorder="reorderSortColumns">
          <template #header>
            <div class="align-right">
              <Button @click="addSortColumn" size="small"><i class="pi pi-plus"></i> Add Sort Column</Button>
            </div>
          </template>
          <Column row-reorder header-style="width: 2rem" :reorderable-column="false" />
          <Column header="Column">
            <template #body="slotProps">
              <InputText v-model="slotProps.data.column" placeholder="Column name" class="full-width"></InputText>
            </template>
          </Column>
          <Column header="Direction">
            <template #body="slotProps">
              <Dropdown v-model="slotProps.data.direction" :options="['ASC', 'DESC']" placeholder="Select" class="full-width"></Dropdown>
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button severity="danger" size="small" @click="removeSortColumn(slotProps.index)"><i class="pi pi-trash"></i></Button>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>

      <div v-else class="form-element">
        <Textarea v-model="lookupJsonText" class="full-width" rows="20" placeholder="Enter lookup configuration JSON"></Textarea>
      </div>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" label="Cancel" severity="secondary" @click="closeLookupDialog"></Button>
        <Button label="Apply" @click="applyLookupConfig"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="manageUserGroupsDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage User Groups
    </template>
    <div class="full-width flex">
      <Dropdown :options="availableGroups"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedGroup"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addSkillToGroupSubmitting" @click="addSkillToGroup(selectedGroup)" :disabled="!selectedGroup" label="Add Group"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 35vh">
      <DataTable :value="skillToManageUserGroups">
        <Column field="name" header="Name"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteSkillUserGroup(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeSkillFromGroupSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>

  <Dialog :visible="manageUsersDialogVisible"
          @update:visible="handleClose">
    <template #header>
      Manage Users
    </template>
    <div class="full-width flex">
      <Dropdown :options="availableUsers"
                option-label="name" option-value="id"
                class="flex-1"
                v-model="selectedUser"
                filter
                :auto-filter-focus="true"
      >
      </Dropdown>
      <Button class="ml" :loading="addSkillToUserSubmitting" @click="addSkillToUser(selectedUser)" :disabled="!selectedUser" label="Add User"></Button>
    </div>
    <ScrollPanel class="scroll-panel" style="max-height: 35vh">
      <DataTable :value="skillToManageUsers">
        <Column field="name" header="Name"></Column>
        <Column header="Actions">
          <template #body="slotProps">
            <Button class="ml" severity="danger" @click="deleteSkillUser(slotProps.data)" label="Remove"></Button>
          </template>
        </Column>
      </DataTable>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Close" :loading="removeSkillFromUserSubmitting"
                severity="secondary" type="button" @click="handleClose"></Button>
      </div>
    </template>
  </Dialog>
  
  <div v-if="hasAdminAccess">
    <ProgressSpinnerContainer v-if="skillsLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">Agent Skills</h2>
        <div class="align-right mr">
          <Button @click="manageSkill(null)"><i class="pi pi-plus"></i> Add Agent Skill</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="skills">
          <Column field="name" header="Name"></Column>
          <Column field="shortDescription" header="Description"></Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button v-if="slotProps.data.platformAccountId > 0" severity="info" @click="manageSkill(slotProps.data)" label="Edit"></Button>
              <Button v-if="slotProps.data.platformAccountId > 0" class="ml" severity="info" @click="manageUserGroups(slotProps.data)" label="Manage Groups"></Button>
              <Button v-if="slotProps.data.platformAccountId > 0" class="ml" severity="info" @click="manageUsers(slotProps.data)" label="Manage Users"></Button>
              <Button v-if="slotProps.data.platformAccountId > 0" class="ml" severity="danger" @click="deleteSkill(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>

  <!-- File Selector Dialog -->
  <Dialog v-model:visible="fileSelectorDialogVisible" 
          @hide="handleFileSelectorHide"
          header="Select Files" 
          :style="{ width: '70vw' }"
          :modal="true"
          :closeOnEscape="true">
    <ScrollPanel style="height: 60vh">
      <FileTreeSelector 
        :selectionMode="true"
        selectionType="files"
        v-model="tempSelectedFileIds"
        :preloadFileIds="vectorSourceFileIds"
        @selection-change="onFileSelectionChange"
      />
    </ScrollPanel>
    <template #footer>
      <Button label="Cancel" icon="pi pi-times" @click="cancelFileSelection" class="p-button-text" />
      <Button label="OK" icon="pi pi-check" @click="confirmFileSelection" />
    </template>
  </Dialog>

  <!-- Directory Selector Dialog -->
  <Dialog v-model:visible="directorySelectorDialogVisible" 
          @hide="handleDirectorySelectorHide"
          header="Select Directories" 
          :style="{ width: '70vw' }"
          :modal="true"
          :closeOnEscape="true">
    <ScrollPanel style="height: 60vh">
      <FileTreeSelector 
        :selectionMode="true"
        selectionType="directories"
        v-model="tempSelectedDirectoryIds"
        :preloadDirectoryIds="vectorSourceDirectoryIds"
        @selection-change="onDirectorySelectionChange"
      />
    </ScrollPanel>
    <template #footer>
      <Button label="Cancel" icon="pi pi-times" @click="cancelDirectorySelection" class="p-button-text" />
      <Button label="OK" icon="pi pi-check" @click="confirmDirectorySelection" />
    </template>
  </Dialog>
</template>

<script setup>
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import FileTreeSelector from "../components/FileTreeSelector.vue";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useSkills, useUsers, useKnowledgeSources } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post, apiGetAll } from "../util/api.js";
import { decodeSkill, decodeSkillAccessGrant, decodeSkillStep, decodeUserGroupMembership } from "../util/util.js";

const { skills, skillSteps, skillStepTypes, skillAccessGrants, skillsLoading } = useSkills()

const { hasAdminAccess, users, userGroups } = useUsers()

const { knowledgeSources } = useKnowledgeSources()

const toast = useToast()

const confirm = useConfirm()

function reorderSkillSteps(event) {
  manageSkillSteps.value.splice(event.dropIndex, 0, manageSkillSteps.value.splice(event.dragIndex, 1)[0]);
  const compareFunc = (v) => {
    if (v === event.dragIndex) {
      return event.dropIndex
    }
    if (event.dragIndex > event.dropIndex) {
      if (v >= event.dropIndex && v < event.dragIndex) {
        return v + 1
      }
      return v
    }
    if (event.dropIndex > event.dragIndex) {
      if (v >= event.dragIndex && v < event.dropIndex) {
        return v - 1
      }
      return v
    }
  }
  invalidSkillStepNames.value = invalidSkillStepNames.value.map(compareFunc)
  invalidSkillStepMachineNames.value = invalidSkillStepMachineNames.value.map(compareFunc)
  invalidSkillStepTypes.value = invalidSkillStepTypes.value.map(compareFunc)
}
function skillStepNameIsInvalid(index) {
  return invalidSkillStepNames.value.includes(index)
}
function skillStepMachineNameIsInvalid(index) {
  return invalidSkillStepMachineNames.value.includes(index)
}
function skillStepMachineNameIsDuplicate(index) {
  return duplicateSkillStepMachineNames.value.includes(index)
}
function skillStepTypeIsInvalid(index) {
  return invalidSkillStepTypes.value.includes(index)
}

async function deleteSkill(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.name + '?',
    header: 'Confirm Delete Agent Skill',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        skillsLoading.value = true
        const request = await deleteRequest('/crbn-tech/ai-skills/' + data.id)
        if (request.id) {
          skills.value = skills.value.filter(q => String(q.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        skillsLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

watch(skillsLoading, (newValue)=> {
  if (!newValue) {
    if (!hasAdminAccess.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

const manageSkillDialogVisible = ref(false)
const manageSkillSubmitting = ref(false)
const isEditingUserSkill = ref(false)

const manageSkillId = ref(null)
const manageSkillName = ref(null)
const manageSkillMachineName = ref(null)
const manageSkillShortDescription = ref(null)
const manageSkillLongDescription = ref(null)
const manageSkillMessageToUser = ref(null)
const manageSkillOrganizational = ref(false)
const manageSkillOutput = ref(null)

const manageSkillSteps = ref(null)

const skillTypeOptions = [
  { label: 'Personal', value: false },
  { label: 'Organizational', value: true }
]

// Function to get machine names of steps that occur BEFORE the current step
function getPreviousStepMachineNames(currentIndex) {
  if (!manageSkillSteps.value) return []
  
  return manageSkillSteps.value
    .slice(0, currentIndex) // Only get steps before current index
    .map(step => step.machineName)
    .filter(name => name && name.trim() !== '')
}

function getAvailableStepTokens(currentIndex) {
  if (!manageSkillSteps.value || currentIndex === null) return []
  
  const tokens = []
  
  // Get all previous steps
  const previousSteps = manageSkillSteps.value.slice(0, currentIndex)
  
  previousSteps.forEach(step => {
    if (!step.machineName) return
    
    const machineName = step.machineName
    const isVectorQuery = isVectorQueryStepType(step.typeId)
    const isArrayStep = step.arrayInputStep && step.arrayInputStep.trim() !== ''
    
    // Add main result token
    if (isVectorQuery) {
      tokens.push({
        name: `{${machineName}:citations}`,
        description: 'Vector search results'
      })
      tokens.push({
        name: `{${machineName}:citations_json}`,
        description: 'Vector search results as JSON'
      })
    } else {
      tokens.push({
        name: `{${machineName}:result}`,
        description: 'Step result'
      })
    }
    
    // Add array aggregation tokens if this is an array step
    if (isArrayStep) {
      if (isVectorQuery) {
        tokens.push({
          name: `{${machineName}:all_citations}`,
          description: 'All citations from array processing'
        })
      } else {
        tokens.push({
          name: `{${machineName}:all_results}`,
          description: 'All results from array processing'
        })
      }
    }
  })
  
  return tokens
}

// Helper functions to determine which fields to show based on step type
function getStepTypeMachineName(typeId) {
  if (!typeId) return null
  const stepType = skillStepTypes.value.find(st => String(st.id) === String(typeId))
  return stepType?.machineName || null
}

// Step type checking functions
function isJumpStepType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName && (
    machineName === 'JUMP_IF_VALUE' || 
    machineName === 'JUMP_IF_NOT_VALUE' ||
    machineName === 'JUMP_IF_EMPTY' ||
    machineName === 'JUMP_IF_NOT_EMPTY'
  )
}

function showJumpCompareValue(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'JUMP_IF_VALUE' || machineName === 'JUMP_IF_NOT_VALUE'
}

function isVectorQueryStepType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'VECTOR_QUERY'
}

function isLookupStepType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'LOOKUP'
}

function isSkillStepType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'SKILL'
}

function isExpandValuesStepType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'EXPAND_VALUES'
}

function isSplitTextStepType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'SPLIT_TEXT'
}

function isFileContentsStepType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'FILE_CONTENTS'
}

function isExitSkillType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'EXIT_SKILL'
}

function isLLMQueryStepType(typeId) {
  const machineName = getStepTypeMachineName(typeId)
  return machineName === 'LLM_QUERY'
}

// Helper functions for showing fields
function showArrayInputField(typeId) {
  // Show for all step types (any step can potentially take array input)
  return true
}

function showCachedParentField(typeId) {
  // Show for all step types that might need cached parent data
  return true
}

function showSystemMessage(typeId) {
  return isSkillStepType(typeId) || isLLMQueryStepType(typeId)
}

function showUserMessage(typeId) {
  // Hide User Message for LOOKUP step type
  return !isLookupStepType(typeId)
}

function getUserMessageLabel(typeId) {
  if (isExitSkillType(typeId)) {
    return 'Output Message'
  } else if (isJumpStepType(typeId)) {
    return 'Input Value'
  } else if (isVectorQueryStepType(typeId)) {
    return 'Search Query'
  } else {
    return 'User Message'
  }
}

function getAvailableSkillsForExecution() {
  // Return all skills except the current one being edited
  if (!skills.value) return []
  
  return skills.value.filter(skill => {
    // Exclude the current skill being edited
    if (manageSkillId.value && String(skill.id) === String(manageSkillId.value)) {
      return false
    }
    return true
  })
}

// Step configuration dialog state
const stepConfigDialogVisible = ref(false)
const currentStepIndex = ref(null)
const currentStepConfig = ref({})

// Vector source state
const vectorSourceType = ref('all')
const vectorSourceFileIndex = ref(0)
const vectorSourceFileIds = ref([])
const vectorSourceDirectoryIds = ref([])
const vectorSourceKnowledgeSourceIds = ref([])

// File/Directory selector dialog state
const fileSelectorDialogVisible = ref(false)
const directorySelectorDialogVisible = ref(false)
const tempSelectedFileIds = ref([])
const tempSelectedDirectoryIds = ref([])
const selectedFiles = ref([])
const selectedDirectories = ref([])
const loadingSelectedItems = ref(false)

function parseVectorSource(vectorSource) {
  if (!vectorSource || vectorSource.trim() === '') {
    return { type: 'all', fileIndex: 0, fileIds: [], directoryIds: [], knowledgeSourceIds: [] }
  }
  
  const str = vectorSource.trim()
  
  // Check for "all"
  if (str === 'all') {
    return { type: 'all', fileIndex: 0, fileIds: [], directoryIds: [], knowledgeSourceIds: [] }
  }
  
  // Check for "input_files"
  if (str === 'input_files') {
    return { type: 'input_files', fileIndex: 0, fileIds: [], directoryIds: [], knowledgeSourceIds: [] }
  }
  
  // Check for "input_file[n]"
  const inputFileMatch = str.match(/^input_file\[(\d+)\]$/)
  if (inputFileMatch) {
    return { type: 'input_file_index', fileIndex: parseInt(inputFileMatch[1]), fileIds: [], directoryIds: [], knowledgeSourceIds: [] }
  }
  
  // Check for "directory_id: x,y,z"
  if (str.startsWith('directory_id:')) {
    const ids = str.substring('directory_id:'.length).trim().split(',')
      .map(id => {
        const trimmed = id.trim()
        const parsed = parseInt(trimmed)
        return isNaN(parsed) ? trimmed : parsed
      })
      .filter(id => id !== '' && id !== null && id !== undefined)
    return { type: 'directories', fileIndex: 0, fileIds: [], directoryIds: ids, knowledgeSourceIds: [] }
  }
  
  // Check for "knowledge_source_id: x,y,z"
  if (str.startsWith('knowledge_source_id:')) {
    const ids = str.substring('knowledge_source_id:'.length).trim().split(',')
      .map(id => parseInt(id.trim()))
      .filter(id => !isNaN(id))
    return { type: 'knowledge_sources', fileIndex: 0, fileIds: [], directoryIds: [], knowledgeSourceIds: ids }
  }
  
  // Otherwise assume it's comma-separated file IDs
  const ids = str.split(',')
    .map(id => {
      const trimmed = id.trim()
      const parsed = parseInt(trimmed)
      return isNaN(parsed) ? trimmed : parsed
    })
    .filter(id => id !== '' && id !== null && id !== undefined)
  return { type: 'files', fileIndex: 0, fileIds: ids, directoryIds: [], knowledgeSourceIds: [] }
}

function buildVectorSource() {
  switch (vectorSourceType.value) {
    case 'all':
      return 'all'
    case 'input_files':
      return 'input_files'
    case 'input_file_index':
      return `input_file[${vectorSourceFileIndex.value || 0}]`
    case 'files':
      // Handle both numeric and string IDs
      const fileIds = vectorSourceFileIds.value
        .filter(id => id !== null && id !== undefined && String(id).trim() !== '')
        .join(',')
      return fileIds
    case 'directories':
      // Handle both numeric and string IDs
      const dirIds = vectorSourceDirectoryIds.value
        .filter(id => id !== null && id !== undefined && String(id).trim() !== '')
        .join(',')
      return dirIds ? `directory_id: ${dirIds}` : ''
    case 'knowledge_sources':
      const ksIds = vectorSourceKnowledgeSourceIds.value
        .filter(id => id !== null && id !== undefined)
        .join(',')
      return ksIds ? `knowledge_source_id: ${ksIds}` : ''
    default:
      return 'all'
  }
}

// Computed properties for displaying selected files and directories
const selectedFilesDisplay = computed(() => selectedFiles.value)
const selectedDirectoriesDisplay = computed(() => selectedDirectories.value)

// File selector functions
const tempSelectedFiles = ref([])

function openFileSelector() {
  tempSelectedFileIds.value = [...vectorSourceFileIds.value]
  fileSelectorDialogVisible.value = true
}

function onFileSelectionChange(selection) {
  tempSelectedFiles.value = selection
}

function cancelFileSelection() {
  tempSelectedFileIds.value = []
  tempSelectedFiles.value = []
  fileSelectorDialogVisible.value = false
}

function confirmFileSelection() {
  vectorSourceFileIds.value = [...tempSelectedFileIds.value]
  selectedFiles.value = [...tempSelectedFiles.value]
  tempSelectedFileIds.value = []
  tempSelectedFiles.value = []
  fileSelectorDialogVisible.value = false
}

function handleFileSelectorHide() {
  // Dialog is being closed, clean up temp state
  tempSelectedFileIds.value = []
  tempSelectedFiles.value = []
}

function removeSelectedFile(fileId) {
  vectorSourceFileIds.value = vectorSourceFileIds.value.filter(id => String(id) !== String(fileId))
  selectedFiles.value = selectedFiles.value.filter(file => String(file.id) !== String(fileId))
}

// Directory selector functions
const tempSelectedDirectories = ref([])

function openDirectorySelector() {
  tempSelectedDirectoryIds.value = [...vectorSourceDirectoryIds.value]
  directorySelectorDialogVisible.value = true
}

function onDirectorySelectionChange(selection) {
  tempSelectedDirectories.value = selection
}

function cancelDirectorySelection() {
  tempSelectedDirectoryIds.value = []
  tempSelectedDirectories.value = []
  directorySelectorDialogVisible.value = false
}

function confirmDirectorySelection() {
  vectorSourceDirectoryIds.value = [...tempSelectedDirectoryIds.value]
  selectedDirectories.value = [...tempSelectedDirectories.value]
  tempSelectedDirectoryIds.value = []
  tempSelectedDirectories.value = []
  directorySelectorDialogVisible.value = false
}

function handleDirectorySelectorHide() {
  // Dialog is being closed, clean up temp state
  tempSelectedDirectoryIds.value = []
  tempSelectedDirectories.value = []
}

function removeSelectedDirectory(directoryId) {
  vectorSourceDirectoryIds.value = vectorSourceDirectoryIds.value.filter(id => String(id) !== String(directoryId))
  selectedDirectories.value = selectedDirectories.value.filter(dir => String(dir.id) !== String(directoryId))
}

// Watch vector source changes and update the step config
watch([vectorSourceType, vectorSourceFileIndex, vectorSourceFileIds, vectorSourceDirectoryIds, vectorSourceKnowledgeSourceIds], () => {
  if (currentStepConfig.value && stepConfigDialogVisible.value) {
    currentStepConfig.value.vectorSource = buildVectorSource()
  }
}, { deep: true })

function openStepConfigDialog(index) {
  currentStepIndex.value = index
  // Create a reference to the step (not a copy, so changes are direct)
  currentStepConfig.value = manageSkillSteps.value[index]
  
  // Parse vector source and set UI state
  const parsed = parseVectorSource(currentStepConfig.value.vectorSource)
  vectorSourceType.value = parsed.type
  vectorSourceFileIndex.value = parsed.fileIndex
  vectorSourceFileIds.value = parsed.fileIds
  vectorSourceDirectoryIds.value = parsed.directoryIds
  vectorSourceKnowledgeSourceIds.value = parsed.knowledgeSourceIds
  
  // Open dialog immediately
  stepConfigDialogVisible.value = true
  
  // Load file/directory objects for display in the background
  loadSelectedFilesAndDirectories()
}

async function loadSelectedFilesAndDirectories() {
  loadingSelectedItems.value = true
  
  try {
    // Load files if there are any selected
    if (vectorSourceFileIds.value.length > 0) {
      try {
        const fileIdsStr = vectorSourceFileIds.value.join(',')
        const query = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"id":{"$in":[${fileIdsStr}]}}`
        const files = await apiGetAll(`/v1/files${query}`)
        selectedFiles.value = files
      } catch (error) {
        console.error('Error loading selected files:', error)
        selectedFiles.value = []
      }
    } else {
      selectedFiles.value = []
    }
    
    // Load directories if there are any selected
    if (vectorSourceDirectoryIds.value.length > 0) {
      try {
        const dirIdsStr = vectorSourceDirectoryIds.value.join(',')
        const query = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"id":{"$in":[${dirIdsStr}]}}`
        const directories = await apiGetAll(`/v1/file-directories${query}`)
        selectedDirectories.value = directories
      } catch (error) {
        console.error('Error loading selected directories:', error)
        selectedDirectories.value = []
      }
    } else {
      selectedDirectories.value = []
    }
  } finally {
    loadingSelectedItems.value = false
  }
}

function closeStepConfigDialog() {
  stepConfigDialogVisible.value = false
  currentStepIndex.value = null
  currentStepConfig.value = {}
  
  // Reset vector source state
  vectorSourceType.value = 'all'
  vectorSourceFileIndex.value = 0
  vectorSourceFileIds.value = []
  vectorSourceDirectoryIds.value = []
  vectorSourceKnowledgeSourceIds.value = []
  selectedFiles.value = []
  selectedDirectories.value = []
  loadingSelectedItems.value = false // Clear loading state immediately
}

// Lookup dialog state
const lookupDialogVisible = ref(false)
const lookupStepIndex = ref(null)
const lookupEditorMode = ref('visual')
const isRevertingLookupMode = ref(false) // Flag to prevent watch recursion

// Computed properties to determine which dialog should handle escape key
// Only the topmost (most recently opened) dialog should close on escape
const manageSkillDialogCloseOnEscape = computed(() => {
  return !stepConfigDialogVisible.value && !lookupDialogVisible.value && !fileSelectorDialogVisible.value && !directorySelectorDialogVisible.value
})

const stepConfigDialogCloseOnEscape = computed(() => {
  return stepConfigDialogVisible.value && !lookupDialogVisible.value && !fileSelectorDialogVisible.value && !directorySelectorDialogVisible.value
})

const lookupDialogCloseOnEscape = computed(() => {
  return lookupDialogVisible.value && !fileSelectorDialogVisible.value && !directorySelectorDialogVisible.value
})
const lookupEditorModes = [
  { label: 'Visual Editor', value: 'visual' },
  { label: 'JSON', value: 'json' }
]

// Watch for editor mode changes to sync data
watch(lookupEditorMode, (newMode, oldMode) => {
  // Skip watch if we're reverting due to an error (prevents overwriting user's invalid JSON)
  if (isRevertingLookupMode.value) {
    isRevertingLookupMode.value = false
    return
  }
  
  if (newMode === 'json' && oldMode === 'visual') {
    // Switching from visual to JSON - build JSON from visual state
    syncVisualToJson()
  } else if (newMode === 'visual' && oldMode === 'json') {
    // Switching from JSON to visual - parse JSON into visual state
    syncJsonToVisual()
  }
})

function syncVisualToJson() {
  const config = {}
  
  if (lookupFiltersArray.value.length > 0) {
    config.filters = lookupFiltersArray.value
      .filter(f => f.column && f.condition)
      .map(filter => {
        const result = {
          column: filter.column,
          condition: filter.condition
        }
        
        if (filter.condition === 'GEO_WITHIN_DISTANCE') {
          if (filter.geoMode === 'latlon') {
            result.latlon = filter.latlon
          } else {
            result.lat = filter.lat
            result.lon = filter.lon
          }
          result.distance_meters = filter.distance_meters
        } else if (filter.condition === 'IN') {
          // Handle IN - store as array and filter out empty values
          if (Array.isArray(filter.value)) {
            result.value = filter.value.filter(v => v !== null && v !== undefined && v !== '')
          } else {
            result.value = []
          }
        } else if (filter.condition === 'BETWEEN') {
          result.value = filter.value
        } else {
          result.value = filter.value
        }
        
        return result
      })
  }
  
  if (lookupOutputDistanceEnabled.value) {
    config.output_distance = {
      column: lookupOutputDistance.value.column,
      alias: lookupOutputDistance.value.alias,
      srid: lookupOutputDistance.value.srid,
      unit: lookupOutputDistance.value.unit
    }
    if (lookupOutputDistance.value.geoMode === 'latlon') {
      config.output_distance.latlon = lookupOutputDistance.value.latlon
    } else {
      config.output_distance.lat = lookupOutputDistance.value.lat
      config.output_distance.lon = lookupOutputDistance.value.lon
    }
  }
  
  if (lookupSortColumns.value.length > 0) {
    config.sort_columns = lookupSortColumns.value
      .filter(s => s.column)
      .map(s => ({
        column: s.column,
        direction: s.direction
      }))
  }
  
  lookupJsonText.value = JSON.stringify(config, null, 2)
}

function syncJsonToVisual() {
  try {
    const config = JSON.parse(lookupJsonText.value || '{}')
    
    // Load filters
    lookupFiltersArray.value = (config.filters || []).map(filter => {
      if (filter.condition === 'GEO_WITHIN_DISTANCE') {
        return {
          column: filter.column || '',
          condition: filter.condition,
          geoMode: filter.latlon ? 'latlon' : 'latlon_separate',
          latlon: filter.latlon || '',
          lat: filter.lat || '',
          lon: filter.lon || '',
          distance_meters: filter.distance_meters || null
        }
      } else if (filter.condition === 'BETWEEN') {
        return {
          column: filter.column || '',
          condition: filter.condition,
          value: Array.isArray(filter.value) ? filter.value : ['', '']
        }
      } else if (filter.condition === 'IN') {
        // Keep as array for Chips component, but also handle comma-separated strings
        let valueArray = []
        if (Array.isArray(filter.value)) {
          valueArray = filter.value
        } else if (typeof filter.value === 'string' && filter.value.trim() !== '') {
          // Parse comma-separated string into array for Chips
          valueArray = filter.value.split(',').map(v => v.trim()).filter(v => v !== '')
        }
        return {
          column: filter.column || '',
          condition: filter.condition,
          value: valueArray
        }
      } else {
        return {
          column: filter.column || '',
          condition: filter.condition,
          value: filter.value || ''
        }
      }
    })
    
    // Load output distance
    if (config.output_distance) {
      lookupOutputDistanceEnabled.value = true
      lookupOutputDistance.value = {
        column: config.output_distance.column || '',
        geoMode: config.output_distance.latlon ? 'latlon' : 'latlon_separate',
        latlon: config.output_distance.latlon || '',
        lat: config.output_distance.lat || '',
        lon: config.output_distance.lon || '',
        alias: config.output_distance.alias || '',
        srid: config.output_distance.srid || 4326,
        unit: config.output_distance.unit || 'MILE'
      }
    } else {
      lookupOutputDistanceEnabled.value = false
    }
    
    // Load sort columns
    lookupSortColumns.value = (config.sort_columns || []).map(sort => ({
      column: sort.column || '',
      direction: sort.direction || 'ASC'
    }))
  } catch (e) {
    console.error('Error parsing JSON:', e)
    toast.add({
      severity: 'error',
      group: 'mainToast',
      life: 5000,
      detail: 'Invalid JSON format. Please fix errors before switching to visual mode.'
    })
    // Revert to JSON mode without triggering watch (preserves user's invalid JSON)
    isRevertingLookupMode.value = true
    lookupEditorMode.value = 'json'
  }
}

const lookupFiltersArray = ref([])
const lookupOutputDistanceEnabled = ref(false)
const lookupOutputDistance = ref({
  column: '',
  geoMode: 'latlon',
  latlon: '',
  lat: '',
  lon: '',
  alias: '',
  srid: 4326,
  unit: 'MILE'
})
const lookupSortColumns = ref([])
const lookupJsonText = ref('')

const conditionOptions = ['=', 'IN', 'BETWEEN', 'LIKE', 'GEO_WITHIN_DISTANCE']
const geoModeOptions = [
  { label: 'LatLon (Single)', value: 'latlon' },
  { label: 'Lat + Lon (Separate)', value: 'latlon_separate' }
]

function openLookupDialog(index) {
  lookupStepIndex.value = index
  const step = manageSkillSteps.value[index]
  
  // Parse existing lookup configuration
  try {
    if (step.lookupFilters && step.lookupFilters.trim() !== '') {
      const config = JSON.parse(step.lookupFilters)
      
      // Load filters
      lookupFiltersArray.value = (config.filters || []).map(filter => {
        if (filter.condition === 'GEO_WITHIN_DISTANCE') {
          return {
            column: filter.column || '',
            condition: filter.condition,
            geoMode: filter.latlon ? 'latlon' : 'latlon_separate',
            latlon: filter.latlon || '',
            lat: filter.lat || '',
            lon: filter.lon || '',
            distance_meters: filter.distance_meters || null
          }
        } else if (filter.condition === 'BETWEEN') {
          return {
            column: filter.column || '',
            condition: filter.condition,
            value: Array.isArray(filter.value) ? filter.value : ['', '']
          }
        } else if (filter.condition === 'IN') {
          // Keep as array for Chips component, but also handle comma-separated strings
          let valueArray = []
          if (Array.isArray(filter.value)) {
            valueArray = filter.value
          } else if (typeof filter.value === 'string' && filter.value.trim() !== '') {
            // Parse comma-separated string into array for Chips
            valueArray = filter.value.split(',').map(v => v.trim()).filter(v => v !== '')
          }
          return {
            column: filter.column || '',
            condition: filter.condition,
            value: valueArray
          }
        } else {
          return {
            column: filter.column || '',
            condition: filter.condition,
            value: filter.value || ''
          }
        }
      })
      
      // Load output distance
      if (config.output_distance) {
        lookupOutputDistanceEnabled.value = true
        lookupOutputDistance.value = {
          column: config.output_distance.column || '',
          geoMode: config.output_distance.latlon ? 'latlon' : 'latlon_separate',
          latlon: config.output_distance.latlon || '',
          lat: config.output_distance.lat || '',
          lon: config.output_distance.lon || '',
          alias: config.output_distance.alias || '',
          srid: config.output_distance.srid || 4326,
          unit: config.output_distance.unit || 'MILE'
        }
      } else {
        lookupOutputDistanceEnabled.value = false
      }
      
      // Load sort columns
      lookupSortColumns.value = (config.sort_columns || []).map(sort => ({
        column: sort.column || '',
        direction: sort.direction || 'ASC'
      }))
      
      lookupJsonText.value = JSON.stringify(config, null, 2)
    } else {
      // Initialize empty
      lookupFiltersArray.value = []
      lookupOutputDistanceEnabled.value = false
      lookupOutputDistance.value = {
        column: '',
        geoMode: 'latlon',
        latlon: '',
        lat: '',
        lon: '',
        alias: '',
        srid: 4326,
        unit: 'MILE'
      }
      lookupSortColumns.value = []
      lookupJsonText.value = ''
    }
  } catch (e) {
    console.error('Error parsing lookup config:', e)
    lookupFiltersArray.value = []
    lookupOutputDistanceEnabled.value = false
    lookupSortColumns.value = []
    lookupJsonText.value = step.lookupFilters || ''
  }
  
  lookupDialogVisible.value = true
}

function closeLookupDialog() {
  lookupDialogVisible.value = false
  lookupStepIndex.value = null
}

function addLookupFilter() {
  lookupFiltersArray.value.push({
    column: '',
    condition: '=',
    value: '',
    geoMode: 'latlon',
    latlon: '',
    lat: '',
    lon: '',
    distance_meters: null
  })
}

function removeLookupFilter(index) {
  lookupFiltersArray.value.splice(index, 1)
}

function addSortColumn() {
  lookupSortColumns.value.push({
    column: '',
    direction: 'ASC'
  })
}

function removeSortColumn(index) {
  lookupSortColumns.value.splice(index, 1)
}

function reorderSortColumns(event) {
  lookupSortColumns.value = event.value
}

function applyLookupConfig() {
  if (lookupStepIndex.value === null) return
  
  let config = {}
  
  if (lookupEditorMode.value === 'visual') {
    // Build config from visual editor
    config.filters = lookupFiltersArray.value
      .filter(f => f.column && f.condition)
      .map(filter => {
        const result = {
          column: filter.column,
          condition: filter.condition
        }
        
        if (filter.condition === 'GEO_WITHIN_DISTANCE') {
          if (filter.geoMode === 'latlon') {
            result.latlon = filter.latlon
          } else {
            result.lat = filter.lat
            result.lon = filter.lon
          }
          result.distance_meters = filter.distance_meters
        } else if (filter.condition === 'IN') {
          // Handle IN - store as array and filter out empty values
          if (Array.isArray(filter.value)) {
            result.value = filter.value.filter(v => v !== null && v !== undefined && v !== '')
          } else {
            result.value = []
          }
        } else if (filter.condition === 'BETWEEN') {
          result.value = filter.value
        } else {
          result.value = filter.value
        }
        
        return result
      })
    
    if (lookupOutputDistanceEnabled.value) {
      config.output_distance = {
        column: lookupOutputDistance.value.column,
        alias: lookupOutputDistance.value.alias,
        srid: lookupOutputDistance.value.srid,
        unit: lookupOutputDistance.value.unit
      }
      if (lookupOutputDistance.value.geoMode === 'latlon') {
        config.output_distance.latlon = lookupOutputDistance.value.latlon
      } else {
        config.output_distance.lat = lookupOutputDistance.value.lat
        config.output_distance.lon = lookupOutputDistance.value.lon
      }
    }
    
    if (lookupSortColumns.value.length > 0) {
      config.sort_columns = lookupSortColumns.value
        .filter(s => s.column)
        .map(s => ({
          column: s.column,
          direction: s.direction
        }))
    }
  } else {
    // Parse from JSON
    try {
      config = JSON.parse(lookupJsonText.value)
    } catch (e) {
      toast.add({
        severity: 'error',
        group: 'mainToast',
        life: 5000,
        detail: 'Invalid JSON format'
      })
      return
    }
  }
  
  // Store as JSON string
  manageSkillSteps.value[lookupStepIndex.value].lookupFilters = JSON.stringify(config)
  
  closeLookupDialog()
}

const manageSkillNameInvalid = ref(false)
const manageSkillMachineNameInvalid = ref(false)
const manageSkillShortDescriptionInvalid = ref(false)
const manageSkillLongDescriptionInvalid = ref(false)
const manageSkillMessageToUserInvalid = ref(false)

const invalidSkillStepNames = ref([])
const invalidSkillStepMachineNames = ref([])
const duplicateSkillStepMachineNames = ref([])
const invalidSkillStepTypes = ref([])

function manageSkillNameChange() {
  manageSkillNameInvalid.value = !manageSkillName.value
}
function manageSkillMachineNameChange() {
  manageSkillMachineNameInvalid.value = !manageSkillMachineName.value
}
function manageSkillShortDescriptionChange() {
  manageSkillShortDescriptionInvalid.value = !manageSkillShortDescription.value
}
function manageSkillLongDescriptionChange() {
  manageSkillLongDescriptionInvalid.value = !manageSkillLongDescription.value
}
function manageSkillMessageToUserChange() {
  manageSkillMessageToUserInvalid.value = !manageSkillMessageToUser.value
}

function skillStepNameChange(i) {
  invalidSkillStepNames.value = invalidSkillStepNames.value.filter(v => v !== i)
}
function skillStepMachineNameChange(i) {
  invalidSkillStepMachineNames.value = invalidSkillStepMachineNames.value.filter(v => v !== i)
  duplicateSkillStepMachineNames.value = duplicateSkillStepMachineNames.value.filter(v => v !== i)
}
function skillStepTypeChange(i) {
  invalidSkillStepTypes.value = invalidSkillStepTypes.value.filter(v => v !== i)
}


function addSkillStep() {
  manageSkillSteps.value.push(
      {
        name: '',
        machineName: '',
        typeId: null,
        description: '',
        systemMessage: '{system_message}',
        userMessage: '{user_message}',
        disableModes: false,
        arrayInputStep: '',
        jumpCompareValue: '',
        jumpToStep: '',
        vectorSource: '',
        cachedParentStep: '',
        lookupTableName: '',
        lookupOutputColumns: '',
        lookupFilters: '',
        lookupLimit: null,
        aiSkillToExecuteId: null,
        expandValuesSourceStep: '',
        splitTextMaxTokens: null,
        fileContentsInputFile: 0
      }
  )
}

function confirmDeleteStep(index) {
  const stepName = manageSkillSteps.value[index]?.name || 'this step'
  confirm.require({
    message: `Are you sure you want to delete ${stepName}?`,
    header: 'Confirm Delete Step',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    accept: () => {
      deleteStep(index)
    }
  })
}

function deleteStep(index) {
  // Remove validation errors for this index
  invalidSkillStepNames.value = invalidSkillStepNames.value.filter(i => i !== index).map(i => i > index ? i - 1 : i)
  invalidSkillStepMachineNames.value = invalidSkillStepMachineNames.value.filter(i => i !== index).map(i => i > index ? i - 1 : i)
  duplicateSkillStepMachineNames.value = duplicateSkillStepMachineNames.value.filter(i => i !== index).map(i => i > index ? i - 1 : i)
  invalidSkillStepTypes.value = invalidSkillStepTypes.value.filter(i => i !== index).map(i => i > index ? i - 1 : i)
  
  // Remove the step from the array
  manageSkillSteps.value.splice(index, 1)
}


function handleClose() {
  manageSkillId.value = null
  manageSkillName.value = null
  manageSkillMachineName.value = null
  manageSkillShortDescription.value = null
  manageSkillLongDescription.value = null
  manageSkillMessageToUser.value = null
  manageSkillOrganizational.value = false
  manageSkillOutput.value = null

  invalidSkillStepNames.value = []
  invalidSkillStepMachineNames.value = []
  duplicateSkillStepMachineNames.value = []
  invalidSkillStepTypes.value = []

  manageSkillSteps.value = []

  manageSkillNameInvalid.value = false
  manageSkillMachineNameInvalid.value = false
  manageSkillShortDescriptionInvalid.value = false
  manageSkillLongDescriptionInvalid.value = false
  manageSkillMessageToUserInvalid.value = false

  manageSkillDialogVisible.value = false
  manageSkillSubmitting.value = false
  isEditingUserSkill.value = false
  
  skillToManage.value = null
  selectedGroup.value = null
  selectedUser.value = null

  addSkillToGroupSubmitting.value = false
  removeSkillFromGroupSubmitting.value = false
  manageUserGroupsDialogVisible.value = false

  addSkillToUserSubmitting.value = false
  removeSkillFromUserSubmitting.value = false
  manageUsersDialogVisible.value = false
  
}

function manageUserGroups(skill) {
  skillToManage.value = {
    id: skill.id,
    name: skill.name
  }
  manageUserGroupsDialogVisible.value = true
}
function manageUsers(skill) {
  skillToManage.value = {
    id: skill.id,
    name: skill.name
  }
  manageUsersDialogVisible.value = true
}

function manageSkill(data) {
  // Check if this is a user skill and set the flag
  isEditingUserSkill.value = data?.metadata?.userSkill === true
  
  manageSkillDialogVisible.value = true
  manageSkillId.value = data?.id
  manageSkillName.value = data?.name
  manageSkillMachineName.value = data?.machineName
  manageSkillShortDescription.value = data?.shortDescription
  manageSkillLongDescription.value = data?.longDescription
  manageSkillMessageToUser.value = data?.messageToUser
  manageSkillOrganizational.value = data?.organizational || false
  manageSkillOutput.value = data?.output
  invalidSkillStepMachineNames.value = []
  duplicateSkillStepMachineNames.value = []
  invalidSkillStepTypes.value = []

  manageSkillSteps.value = []
  if (data?.id) {
    manageSkillSteps.value = skillSteps.value
        .filter(s => String(s.skillId) === String(data.id))
        .slice().sort((a, b) => a.stepOrder - b.stepOrder)
        .map(({ id, name, machineName, typeId, description, systemMessage, userMessage, disableModes, inputVariables, aiSkillToExecuteId }) => {
          // Determine if this is an array input or jump step based on input_variables
          const isArrayInput = inputVariables?.input_type === 'array'
          const isJumpStep = inputVariables?.jump_to_step !== undefined
          
          return {
            id,
            name,
            machineName,
            typeId: skillStepTypes.value.find(st => String(st.id) === String(typeId))?.id,
            description,
            systemMessage,
            userMessage,
            disableModes: disableModes || false,
            arrayInputStep: isArrayInput ? (inputVariables?.input_step || '') : '',
            jumpCompareValue: inputVariables?.compare_value || '',
            jumpToStep: inputVariables?.jump_to_step || '',
            vectorSource: inputVariables?.vector_source || '',
            cachedParentStep: inputVariables?.cached_parent_step || '',
            lookupTableName: inputVariables?.table_name || '',
            lookupOutputColumns: inputVariables?.output_columns || '',
            lookupFilters: inputVariables?.filters ? JSON.stringify(inputVariables.filters) : '',
            lookupLimit: inputVariables?.limit || null,
            aiSkillToExecuteId: aiSkillToExecuteId || null,
            expandValuesSourceStep: inputVariables?.source_step || '',
            splitTextMaxTokens: inputVariables?.max_tokens ?? null,
            fileContentsInputFile: inputVariables?.input_file ?? 0
          }
    })
  }
  else {
    addSkillStep()
  }
}

function handleError(e) {
  console.log(e)
  manageSkillSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}

function resetValidations() {
  manageSkillNameInvalid.value = false
  manageSkillMachineNameInvalid.value = false
  manageSkillShortDescriptionInvalid.value = false
  manageSkillLongDescriptionInvalid.value = false
  manageSkillMessageToUserInvalid.value = false
  invalidSkillStepMachineNames.value = []
  duplicateSkillStepMachineNames.value = []
  invalidSkillStepTypes.value = []
}

async function saveSkill() {
  let valid = true
  resetValidations()
  if (!manageSkillName.value) {
    manageSkillNameInvalid.value = true
    valid = false
  }
  if (!manageSkillMachineName.value) {
    manageSkillMachineNameInvalid.value = true
    valid = false
  }
  if (!manageSkillShortDescription.value) {
    manageSkillShortDescriptionInvalid.value = true
    valid = false
  }
  if (!manageSkillMessageToUser.value) {
    manageSkillMessageToUserInvalid.value = true
    valid = false
  }

  // Only validate skill steps if not editing a user skill
  if (!isEditingUserSkill.value) {
    const seenMachineNames = new Set();
    const duplicateMachineNames = []
    for (let i = 0; i < manageSkillSteps.value?.length; i++) {
      const machineName = manageSkillSteps.value[i].machineName;
      if (seenMachineNames.has(machineName)) {
        duplicateMachineNames.push(machineName);
      }
      seenMachineNames.add(machineName);
    }

    for (let i = 0; i < manageSkillSteps.value.length; i++) {
      if (!manageSkillSteps.value[i].name) {
        invalidSkillStepNames.value.push(i)
        valid = false
      }

      if (!manageSkillSteps.value[i].machineName) {
        invalidSkillStepMachineNames.value.push(i)
        valid = false
      }
      else if (duplicateMachineNames.includes(manageSkillSteps.value[i].machineName)) {
        duplicateSkillStepMachineNames.value.push(i)
        valid = false
      }

      if (!manageSkillSteps.value[i].typeId) {
        invalidSkillStepTypes.value.push(i)
        valid = false
      }

    }
  }

  if (!valid) {
    return false
  }

  if (manageSkillId.value) {
    const payload = {
      name: manageSkillName.value,
      machine_name: manageSkillMachineName.value,
      short_description: manageSkillShortDescription.value,
      long_description: manageSkillLongDescription.value,
      message_to_user: manageSkillMessageToUser.value,
      organizational: manageSkillOrganizational.value ? 1 : 0,
      output: manageSkillOutput.value
    }
    if (manageSkillOutput.value?.length > 0) {
      payload.output = manageSkillOutput.value
    }
    else {
      payload.output = null
    }
    manageSkillSubmitting.value = true
    try {
      const result = await patch('/crbn-tech/ai-skills/' + manageSkillId.value, payload)
      if (result.id) {
        for (let i = 0; i < skills.value.length; i++) {
          if (String(skills.value[i].id) === String(result.id)) {
            skills.value[i] = decodeSkill(result)
          }
        }
        // Only save steps if not editing a user skill
        if (!isEditingUserSkill.value) {
          await saveSteps(result.id)
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageSkillSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const payload = {
      name: manageSkillName.value,
      machine_name: manageSkillMachineName.value,
      short_description: manageSkillShortDescription.value,
      long_description: manageSkillLongDescription.value,
      message_to_user: manageSkillMessageToUser.value,
      organizational: manageSkillOrganizational.value ? 1 : 0,
      cephisso_platform_accounts_id: selectedAccount.value,
      visibility: 1,
      archived: 0
    }
    if (manageSkillOutput.value?.length > 0) {
      payload.output = manageSkillOutput.value
    }
    else {
      payload.output = null
    }
    manageSkillSubmitting.value = true
    try {
      const result = await post('/crbn-tech/ai-skills', payload)
      if (result.id) {
        skills.value.push(decodeSkill(result))
        await saveSteps(result.id)
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageSkillSubmitting.value = false
      handleError(e)
    }
  }
}

async function saveSteps(skillId) {
  /* Slice the array to make a copy, just in case the dialog is closed or something else changes while it's in the process of saving */
  const steps = manageSkillSteps.value.slice()
  for (let i = 0; i < steps.length; i++ ) {
    const payload = {
      cephisso_platform_accounts_id: selectedAccount.value,
      title: steps[i].name,
      machine_name: steps[i].machineName,
      ai_skills_id: skillId,
      /* @todo ai_skill_steps_id */
      ai_skill_step_types_id: steps[i].typeId,
      description: steps[i].description,
      user_message: steps[i].userMessage,
      system_message: steps[i].systemMessage,
      disable_modes: steps[i].disableModes,
      step_order: i
    }
    
    // Determine step type machine name for conditional logic
    const stepType = skillStepTypes.value.find(st => String(st.id) === String(steps[i].typeId))
    const stepTypeMachineName = stepType?.machineName || ''
    
    // Build input_variables object based on what's set (can be combined)
    const inputVariables = {}
    
    // Add array input if set
    if (steps[i].arrayInputStep && steps[i].arrayInputStep.trim() !== '') {
      inputVariables.input_type = 'array'
      inputVariables.input_step = steps[i].arrayInputStep
    }
    
    // Add jump variables if set
    if (steps[i].jumpToStep && steps[i].jumpToStep.trim() !== '') {
      inputVariables.jump_to_step = steps[i].jumpToStep
      
      // Add compare_value only for JUMP_IF_VALUE and JUMP_IF_NOT_VALUE
      if (stepTypeMachineName === 'JUMP_IF_VALUE' || stepTypeMachineName === 'JUMP_IF_NOT_VALUE') {
        inputVariables.compare_value = steps[i].jumpCompareValue || ''
      }
    }
    
    // Add vector_source for VECTOR_QUERY steps
    if (stepTypeMachineName === 'VECTOR_QUERY' && steps[i].vectorSource && steps[i].vectorSource.trim() !== '') {
      inputVariables.vector_source = steps[i].vectorSource
    }
    
    // Add cached_parent_step if set
    if (steps[i].cachedParentStep && steps[i].cachedParentStep.trim() !== '') {
      inputVariables.cached_parent_step = steps[i].cachedParentStep
    }
    
    // Add LOOKUP variables if set
    if (stepTypeMachineName === 'LOOKUP') {
      if (steps[i].lookupTableName && steps[i].lookupTableName.trim() !== '') {
        inputVariables.table_name = steps[i].lookupTableName
      }
      if (steps[i].lookupOutputColumns && steps[i].lookupOutputColumns.trim() !== '') {
        inputVariables.output_columns = steps[i].lookupOutputColumns
      }
      if (steps[i].lookupFilters && steps[i].lookupFilters.trim() !== '') {
        try {
          inputVariables.filters = JSON.parse(steps[i].lookupFilters)
        } catch (e) {
          console.error('Invalid JSON in lookup filters:', e)
        }
      }
      if (steps[i].lookupLimit !== null && steps[i].lookupLimit !== undefined) {
        inputVariables.limit = steps[i].lookupLimit
      }
    }
    
    // Add source_step for EXPAND_VALUES
    if (stepTypeMachineName === 'EXPAND_VALUES' && steps[i].expandValuesSourceStep && steps[i].expandValuesSourceStep.trim() !== '') {
      inputVariables.source_step = steps[i].expandValuesSourceStep
    }
    
    // Add max_tokens for SPLIT_TEXT
    if (stepTypeMachineName === 'SPLIT_TEXT' && steps[i].splitTextMaxTokens !== null && steps[i].splitTextMaxTokens !== undefined) {
      inputVariables.max_tokens = steps[i].splitTextMaxTokens
    }
    
    // Add input_file for FILE_CONTENTS
    if (stepTypeMachineName === 'FILE_CONTENTS') {
      inputVariables.input_file = steps[i].fileContentsInputFile ?? 0
    }
    
    // Only add input_variables to payload if at least one property was set
    if (Object.keys(inputVariables).length > 0) {
      payload.input_variables = JSON.stringify(inputVariables)
    }
    
    // Add ai_skill_id_to_execute_id for SKILL steps
    if (stepTypeMachineName === 'SKILL' && steps[i].aiSkillToExecuteId) {
      payload.ai_skill_to_execute_id = steps[i].aiSkillToExecuteId
    }
    
    let result;
    if (steps[i].id) {
      result = await patch('/crbn-tech/ai-skill-steps/' + steps[i].id, payload)
      if (result.id) {
        for (let i = 0; i < skillSteps.value.length; i++) {
          if (String(skillSteps.value[i].id) === String(result.id)) {
            skillSteps.value[i] = decodeSkillStep(result)
          }
        }
      }
      else {
        /* @todo handle error */
      }
    }
    else {
      result = await post('/crbn-tech/ai-skill-steps', payload)
      if (result.id) {
        skillSteps.value.push(decodeSkillStep(result))
      }
      else {
        /* @todo handle errors */
      }
    }
    /* @todo handle errors with try/catch*/
  }
  return true
}

const skillToManage = ref(null)

const selectedGroup = ref(null)
const addSkillToGroupSubmitting = ref(false)
const removeSkillFromGroupSubmitting = ref(false)
const manageUserGroupsDialogVisible = ref(false)

const skillToManageUserGroups = computed(() => {
  const output = []
  if (!skillToManage.value) {
    return output
  }
  for (let i = 0; i < skillAccessGrants.value.length; i++) {
    if (String(skillAccessGrants.value[i].skillId) === String(skillToManage.value.id)) {
      const group = userGroups.value.find(g => String(g.id) === String(skillAccessGrants.value[i].groupId))
      if (group) {
        output.push({
          skillAccessGrantId: skillAccessGrants.value[i].id,
          ...group
        })
      }
    }
  }
  return output
})

const availableGroups = computed(() => userGroups.value.filter(g => !skillToManageUserGroups.value?.find(a => String(a.groupId) === String(g.id))))

async function addSkillToGroup(id) {
  const payload = {
    ai_skills_id: skillToManage.value.id,
    cephisso_platform_user_groups_id: id,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addSkillToGroupSubmitting.value = true
  const request = await post('/crbn-tech/ai-skill-access-grants', payload)
  if (request.id) {
    skillAccessGrants.value.push(decodeSkillAccessGrant(request))
  }
  else {
    handleError()
  }
  addSkillToGroupSubmitting.value = false
  selectedGroup.value = null
}

function deleteSkillUserGroup(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + skillToManage.value.name + ' from ' + data.name + '?',
    header: 'Confirm User Group Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeSkillFromGroupSubmitting.value = true
        for (let i = 0; i < skillAccessGrants.value?.length; i++) {
          if (
              (String(skillAccessGrants.value[i].groupId) === String(data.id)) &&
              (String(skillAccessGrants.value[i].skillId) === String(skillToManage.value.id))
          ) {
            const request = await deleteRequest('/crbn-tech/ai-skill-access-grants/' + skillAccessGrants.value[i].id)
            if (request.id) {
              skillAccessGrants.value = skillAccessGrants.value.filter(sa => String(sa.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeSkillFromGroupSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

const selectedUser = ref(null)
const addSkillToUserSubmitting = ref(false)
const removeSkillFromUserSubmitting = ref(false)
const manageUsersDialogVisible = ref(false)

const skillToManageUsers = computed(() => {
  const output = []
  if (!skillToManage.value) {
    return output
  }
  for (let i = 0; i < skillAccessGrants.value.length; i++) {
    if (String(skillAccessGrants.value[i].skillId) === String(skillToManage.value.id)) {
      const group = users.value.find(g => String(g.id) === String(skillAccessGrants.value[i].userId))
      if (group) {
        output.push({
          skillAccessGrantId: skillAccessGrants.value[i].id,
          ...group
        })
      }
    }
  }
  return output
})

const availableUsers = computed(() => users.value.filter(g => !skillToManageUsers.value?.find(a => String(a.userId) === String(g.id))))

async function addSkillToUser(id) {
  const payload = {
    ai_skills_id: skillToManage.value.id,
    cephisso_platform_users_id: id,
    cephisso_platform_accounts_id: selectedAccount.value
  }
  addSkillToUserSubmitting.value = true
  const request = await post('/crbn-tech/ai-skill-access-grants', payload)
  if (request.id) {
    skillAccessGrants.value.push(decodeSkillAccessGrant(request))
  }
  else {
    handleError()
  }
  addSkillToUserSubmitting.value = false
  selectedUser.value = null
}

function deleteSkillUser(data) {
  confirm.require({
    message: 'Are you sure you wish to remove ' + skillToManage.value.name + ' from ' + data.name + '?',
    header: 'Confirm User Removal',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        removeSkillFromUserSubmitting.value = true
        for (let i = 0; i < skillAccessGrants.value?.length; i++) {
          if (
              (String(skillAccessGrants.value[i].userId) === String(data.id)) &&
              (String(skillAccessGrants.value[i].skillId) === String(skillToManage.value.id))
          ) {
            const request = await deleteRequest('/crbn-tech/ai-skill-access-grants/' + skillAccessGrants.value[i].id)
            if (request.id) {
              skillAccessGrants.value = skillAccessGrants.value.filter(sa => String(sa.id) !== String(request.id))
            }
            else {
              handleError()
            }
          }
        }
      }
      catch (e) {
        handleError(e)
      }
      removeSkillFromUserSubmitting.value = false
      return true
    },
    reject: () => {
      return true
    }
  })
}

</script>

<style scoped>
.vector-source-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
}

.vector-source-option {
  display: flex;
  flex-direction: column;
}

.selected-items-display {
  margin-top: 0.5rem;
}

.selected-items-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.tokens-info {
  font-size: 0.9rem;
}

.tokens-info p {
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
  font-weight: 600;
}

.tokens-info ul {
  margin: 0.25rem 0 0.5rem 1.5rem;
  padding: 0;
}

.tokens-info li {
  margin-bottom: 0.25rem;
}

.tokens-info code {
  background-color: #f4f4f4;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  color: #d63384;
  font-weight: 600;
}

/* Accordion styling for tokens section */
:deep(.p-accordion) {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}

:deep(.p-accordion .p-accordion-header .p-accordion-header-link) {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  font-size: .75em;
}

:deep(.p-accordion .p-accordion-content) {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-top: none;
}
</style>